import { Chart, ChartTypes, ChartInfoIcon, formatNumber } from "@uicore";
import { NativeSelect, Spinner, Stack, Typography } from "@altimateai/lego";
import dayjs from "dayjs";
import { useClustersCostTrends } from "../hooks";
import { useAppState } from "@/modules/app/useAppContext";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { Bar } from "recharts";
import type { ClustersCostTrendsParams } from "../types";
import { formatChartDate, getComputeTypeChartLabel, getComputeTypeColor } from "../helpers";
import ClusterCostTooltip from "./ClusterCostTooltip";

export type Granularity = ClustersCostTrendsParams["granularity"];

const GRANULARITY_OPTIONS = [
  { value: "day", label: "Daily" },
  { value: "week", label: "Weekly" },
  { value: "month", label: "Monthly" },
];

interface Props {
  workspaceId?: string;
  granularity?: Granularity;
  onGranularityChange?: (value: string) => void;
}

const ClusterCostTrends = ({ workspaceId, granularity = "day", onGranularityChange }: Props) => {
  const { currency } = useAppState();
  const { startDate, endDate } = useDateFromSearchParams();
  const { data, isLoading, error } = useClustersCostTrends({
    start_date: dayjs(startDate).format("YYYY-MM-DD"),
    end_date: dayjs(endDate).format("YYYY-MM-DD"),
    workspace_id: workspaceId,
    granularity,
  });

  const periodMap = new Map<string, Record<string, string | number>>();
  data?.trends.forEach(point => {
    if (!periodMap.has(point.period)) {
      periodMap.set(point.period, { period: point.period });
    }
    const periodData = periodMap.get(point.period)!;
    periodData[point.compute_type] = point.estimated_cost;
  });

  const chartData = Array.from(periodMap.values());
  const computeTypes = data?.compute_types ?? [];

  return (
    <div className="al-w-full al-border-b al-pb-6">
      <Stack direction="row" align="center" justify="between" className="al-mb-4">
        <Stack direction="row" align="center" gap="xs">
          <Typography variant="section-heading">Clusters Cost</Typography>
          <ChartInfoIcon
            title="How to read this chart"
            content="This stacked bar chart shows cluster compute cost split into All Purpose, Jobs, and DLT. Each color is one compute class, and the total bar height is the combined cost for that period. Use the aggregation selector to switch between daily, weekly, or monthly trends."
          />
        </Stack>
        {onGranularityChange && (
          <NativeSelect
            value={granularity}
            onChange={onGranularityChange}
            variant="outline"
            size="sm"
            label="Aggregate by"
            showPlaceholder
            options={GRANULARITY_OPTIONS}
            buttonProps={{ "aria-label": "Aggregate by" }}
          />
        )}
      </Stack>
      {isLoading ? (
        <Stack
          align="center"
          justify="center"
          role="alert"
          aria-label="Loading clusters cost"
          className="al-h-[360px] al-w-full"
        >
          <Spinner size="lg" />
        </Stack>
      ) : error || !data ? (
        <Typography className="al-text-red-500">Failed to load cluster cost trends</Typography>
      ) : (
        <div className="al-h-[380px] al-w-full al-pb-2">
          <Chart
            title=""
            xAxisDataKey="period"
            yAxisLabel={{ value: "Cost" }}
            xAxisLabelFormatter={(value: string) => formatChartDate(value, granularity || "day")}
            yAxisLabelFormatter={(value: string) =>
              formatNumber(Number(value), { currency, maximumFractionDigits: 0 })
            }
            tooltipProps={{
              content: props => <ClusterCostTooltip {...props} granularity={granularity} />,
            }}
            type={ChartTypes.BarChart}
            data={chartData}
            height={360}
            legendProps={{ wrapperStyle: { bottom: -16, paddingTop: 4, fontSize: 12 } }}
          >
            {({ barSize, selectedLegend }: { barSize: number; selectedLegend?: string }) => (
              <>
                {computeTypes.map((computeType, index) => (
                  <Bar
                    key={computeType}
                    barSize={barSize}
                    name={getComputeTypeChartLabel(computeType)}
                    dataKey={computeType}
                    stackId="cluster-cost"
                    fill={getComputeTypeColor(computeType)}
                    radius={index === computeTypes.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                    hide={Boolean(selectedLegend && selectedLegend !== computeType)}
                  />
                ))}
              </>
            )}
          </Chart>
        </div>
      )}
    </div>
  );
};

export default ClusterCostTrends;
